/**
 * Media URL Utility
 *
 * Handles URL transformations for uploaded media files.
 *
 * URL Rules:
 * - /uploads/... → Served directly by nginx (NO /api prefix)
 * - /api/... → Already has API prefix, use as-is
 * - http(s)://... → External URLs, use as-is
 * - Other paths → Add /api prefix for backend API
 */

/**
 * Transforms a media URL to the correct format for serving.
 *
 * @param url - The URL to transform (can be relative or absolute)
 * @returns The correct URL for the media resource
 *
 * @example
 * // Uploads - served by nginx directly
 * getMediaUrl('/uploads/video/abc.webm') → '/uploads/video/abc.webm'
 *
 * // Already has API prefix
 * getMediaUrl('/api/users/1') → '/api/users/1'
 *
 * // External URLs
 * getMediaUrl('https://example.com/image.jpg') → 'https://example.com/image.jpg'
 *
 * // Relative paths (for backward compatibility)
 * getMediaUrl('video/abc.webm') → '/api/uploads/video/abc.webm'
 */
export const getMediaUrl = (url: string | undefined | null): string => {
  if (!url) return ''

  // External URLs - use as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  // Already has /api prefix - use as-is
  if (url.startsWith('/api/')) {
    return url
  }

  // Uploads - served directly by nginx, NO /api prefix
  if (url.startsWith('/uploads/')) {
    return url
  }

  // Legacy /chat-media/ path - convert to /api/uploads/chat-media/
  if (url.startsWith('/chat-media/')) {
    return '/api/uploads' + url
  }

  // Relative paths - add /api/uploads prefix for backward compatibility
  const cleanPath = url.startsWith('/') ? url : `/${url}`
  return `/api/uploads${cleanPath}`
}

/**
 * Gets the full URL for a media resource, using the current origin for relative URLs.
 * Useful for absolute URLs needed in some contexts (e.g., Open Graph tags).
 *
 * @param url - The URL to transform
 * @returns The absolute URL for the media resource
 */
export const getAbsoluteMediaUrl = (url: string | undefined | null): string => {
  const mediaUrl = getMediaUrl(url)
  if (!mediaUrl) return ''

  if (mediaUrl.startsWith('http')) {
    return mediaUrl
  }

  return `${window.location.origin}${mediaUrl}`
}

/**
 * Default avatar URL for users without profile images
 */
export const DEFAULT_AVATAR = '/default-avatar.svg'

/**
 * Gets the profile image URL or returns the default avatar.
 *
 * @param profileImage - The profile image URL (can be null/undefined)
 * @returns The profile image URL or default avatar
 */
export const getProfileImageUrl = (profileImage: string | undefined | null): string => {
  if (!profileImage) return DEFAULT_AVATAR
  return getMediaUrl(profileImage)
}

export default {
  getMediaUrl,
  getAbsoluteMediaUrl,
  getProfileImageUrl,
  DEFAULT_AVATAR
}